/**
 * Where an activation link lands.
 *
 * Reached by everyone's first contact with the portal — a client company's
 * administrator opening their invitation — so it is unauthenticated and sits
 * outside the shell, as a sibling of `/login` rather than a child of it. The
 * split-screen layout (`.auth-screen` / `.auth-panel` / `.auth-form-side`) is
 * the same one `LoginScreen` uses, for the same two reasons documented there:
 * the value panel states the 5 October obligation to someone whose first
 * exposure to the product this may well be, and the language picker is drawn
 * here for the same reason it is drawn on `/login` — the portal's other two
 * language controls both live inside the authenticated shell, and a user who
 * cannot sign in yet has no way to reach them.
 *
 * `p` selects a heading and nothing else. The server neither sends nor reads
 * it: expiry and behaviour come from the token's own row (`purpose`), and
 * there is deliberately no endpoint that discloses a token's purpose, because
 * that would be an unauthenticated oracle over a value somebody may have
 * mistyped. A tampered or absent `p` therefore costs a wrong heading and
 * nothing more — `resetSearchSchema` falls back to the reset wording.
 *
 * `lang` is cosmetic in exactly the same way, and exists to solve a real gap: an invitee's
 * profile locale only becomes known *after* they sign in, which they cannot do until this
 * screen has done its job. The mail that brought them here already knows their locale, so it
 * is carried on the link and applied once on mount, below — with `i18n.changeLanguage()`,
 * never `rememberLanguage()`. `rememberLanguage` would make `hasExplicitLanguage()` true
 * forever, which would make `auth.tsx`'s `applyProfileLanguage` stop applying the signed-in
 * user's own profile language for good — a permanent consequence for a link clicked once. A
 * visitor who has already chosen a language via the picker keeps it: `hasExplicitLanguage()`
 * guards the effect, so their explicit choice outranks a parameter in a mail we generated.
 *

 * The dead-link branch switches on `problem.code === 'TOKEN_INVALID'`, never on the HTTP
 * status. `400` alone is not a safe discriminator here: the route's own body schema
 * (`newPassword` `minLength: 12`, `token` `maxLength: 128`) rejects a request before the
 * handler runs, and Fastify's generic error handler answers that with `400 BAD_REQUEST` —
 * indistinguishable by status from the handler's own `400 TOKEN_INVALID`. A twelve-character
 * floor is well above the habitual minimum, so a short first password is the single most
 * likely input this screen will ever see; branching on status alone told that user their
 * activation link had expired, which sends them back to a link that will fail the same way
 * forever. `code` is the stable identifier for exactly this reason (`app/errors.ts`,
 * `@deca/api`'s `errors.ts`) and `TOKEN_INVALID` is pinned server-side
 * (`backend/test/auth-crud.test.ts`). `minLength={12}` on both fields keeps most users from
 * reaching Ajv's English-shaped sentence at all — not a local copy of a server rule, since 12
 * is the contract's own declared constraint, the same source every type here is generated
 * from.
 *
 * Every other failure — `422` included — falls through to `apiErrorMessage`, which shows the
 * server's own `detail` verbatim: the password rule it states is authoritative, and a second,
 * local wording of it would be a copy that drifts. That includes `PASSWORD_REJECTED`'s only
 * reachable message ("too predictable" — `TOO_SHORT` cannot occur, the schema already refused
 * anything short enough to trigger it), which `errors.code.PASSWORD_REJECTED` translates: the
 * backend does not read `Accept-Language`, so without it an English-locale user gets a
 * Spanish sentence on the one screen that is often their very first.
 *
 * `error` alone cannot say which of those two paths produced it — it is already a translated
 * string by the time it reaches state, and comparing it back against a catalogue key breaks the
 * moment the catalogue is edited or the language changes mid-error. The `TOKEN_INVALID` arm
 * therefore also sets `linkIsDead`, a second, untranslated piece of state that the danger notice
 * reads to decide whether to render a "back to login" action beside the message — the same way
 * out the empty-token branch above already offers, since an expired or already-consumed token is
 * exactly as much of a dead end as a missing one. Every other failure path, including the 422
 * case, clears it, so a short-password retry that follows an expired-link attempt does not carry
 * a stale action link forward.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DecaApiError, unwrap } from '@deca/api'

import logoUrl from '../assets/asgtrans-logo.svg'
import { api } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import i18n, { hasExplicitLanguage } from '../app/i18n.ts'
import { LegalLinks } from '../legal/LegalLinks.tsx'
import { Button, Notice } from '../ui/primitives.tsx'
import { TextInput } from '../ui/form.tsx'
import { Icon } from '../ui/Icon.tsx'
import { LanguagePicker } from '../ui/LanguagePicker.tsx'
import { resetSearchSchema } from './searchSchemas.ts'

const VALUE_POINTS = ['auth.value1', 'auth.value2', 'auth.value3'] as const

export function ResetPasswordScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // `useSearch({ strict: false })` + a manual `.parse`, not `route.useSearch()` — the
  // route object lives in `router.tsx`, which imports this component, and importing it
  // back here would be circular. `DecaListScreen` uses the same idiom.
  const rawSearch = useSearch({ strict: false })
  const { token, p, lang } = resetSearchSchema.parse(rawSearch)

  // The invitation's own language, applied once. Deliberately NOT rememberLanguage(): that writes
  // `deca.language`, which makes hasExplicitLanguage() true forever, which makes auth.tsx stop
  // applying the user's profile language after sign-in. An invited user who never touched the
  // switcher would have their own profile preference silently disabled for good — a permanent
  // consequence for a link they clicked once.
  //
  // Guarded on hasExplicitLanguage() so a visitor who HAS chosen a language keeps it: their explicit
  // choice outranks a parameter in a mail we generated.
  useEffect(() => {
    if (lang && !hasExplicitLanguage()) void i18n.changeLanguage(lang)
  }, [lang])

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Whether the failure is one a fresh link would fix. `error` alone cannot answer that — it is a
  // translated string, so any comparison against it breaks when the catalogue is edited or the
  // language changes mid-error.
  const [linkIsDead, setLinkIsDead] = useState(false)
  const [busy, setBusy] = useState(false)

  const heading = p === 'invite' ? t('reset.titleInvite') : t('reset.titleReset')

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setLinkIsDead(false)

    if (password !== confirm) {
      setError(t('reset.mismatch'))
      return
    }

    setBusy(true)
    try {
      await unwrap(api.POST('/api/v1/auth/password/reset', { body: { token, newPassword: password } }))
      // `replace: true`: this URL carries the token, and it must not sit in history where
      // Back could return the user to a form whose token this same request just consumed.
      void navigate({ to: '/login', search: { reset: true }, replace: true })
    } catch (caught) {
      if (caught instanceof DecaApiError && caught.code === 'TOKEN_INVALID') {
        // Unknown, expired and already-consumed all answer this code alike — the server will
        // not say which, and the portal must not invent the distinction it refuses to make.
        // One local message for all of them, not the server's `detail`. Switching on the code
        // rather than the `400` status matters: the route's own body schema also answers 400
        // for a too-short password, with a different code (`BAD_REQUEST`) — see the module doc.
        setError(t('reset.invalidLink'))
        setLinkIsDead(true)
      } else {
        // 422 (`PASSWORD_REJECTED`) included: `errors.code.PASSWORD_REJECTED` translates its
        // one reachable message, and anything else falls through to the server's own `detail`,
        // which is the authoritative rule — a second local wording of it would be a copy that
        // drifts.
        setError(apiErrorMessage(caught, t))
        setLinkIsDead(false)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-screen">
      <aside className="auth-panel">
        <img src={logoUrl} alt={t('app.operator')} className="auth-panel__logo" />
        <h1 className="auth-panel__headline">{t('app.documentTitle')}</h1>
        <p className="auth-panel__intro">{t('auth.valueIntro')}</p>

        <ul className="auth-panel__points">
          {VALUE_POINTS.map((key) => (
            <li key={key}>
              <Icon name="checkCircle" size={18} />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>

        <p className="auth-panel__legal">{t('app.legalBasis')}</p>
        <LegalLinks className="auth-panel__links" />
      </aside>

      <div className="auth-form-side">
        <div className="auth-form-side__language">
          <LanguagePicker />
        </div>

        <form className="auth-card" onSubmit={(event) => void onSubmit(event)}>
          <h2 className="auth-card__title">{heading}</h2>

          {!token ? (
            // An empty token — no `?token=` at all, a stripped query string, a mail
            // client that truncated the link — is refused here, before a request is
            // even sent. The server would 400 it too, but there is no reason to make
            // the round trip only to show the exact same message a moment later. A dead
            // end needs a way out: `/login` is where a fresh link can be requested.
            <Notice
              tone="danger"
              actions={
                <Link to="/login" className="btn btn--ghost btn--sm">
                  {t('auth.backToLogin')}
                </Link>
              }
            >
              {t('reset.invalidLink')}
            </Notice>
          ) : (
            <>
              {error ? (
                <Notice
                  tone="danger"
                  {...(linkIsDead
                    ? {
                        actions: (
                          <Link to="/login" className="btn btn--ghost btn--sm">
                            {t('auth.backToLogin')}
                          </Link>
                        ),
                      }
                    : {})}
                >
                  {error}
                </Notice>
              ) : null}

              <div className="stack">
                <TextInput
                  label={t('reset.password')}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <TextInput
                  label={t('reset.confirm')}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </div>

              <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={busy}>
                {t('reset.submit')}
              </Button>
            </>
          )}
        </form>
      </div>
    </div>
  )
}
